<template>
  <div class="line-chart-card">
    <!-- Header -->
    <div class="line-chart-card__header">
      <div class="header-left">
        <h2 class="line-chart-card__title">{{ title }}</h2>
        <p class="line-chart-card__subtitle">{{ subtitle }}</p>
      </div>
      <div class="header-right">
        <span class="line-chart-card__total">Total: {{ total }}</span>
      </div>
    </div>

    <!-- Chart / No data -->
    <div class="line-chart-card__canvas-wrapper" :class="{ 'is-empty': total === 0 }">
      <!-- No Data -->
      <div v-if="total === 0" class="line-chart-no-data" role="status" aria-live="polite">
        <div class="line-no-data-illustration">
          <ion-icon name="analytics-outline"></ion-icon>
        </div>
        <h3 class="line-no-data-title">Sin datos en este rango</h3>
        <p class="line-no-data-text">Ajusta fechas o cambia la línea para ver información.</p>
      </div>

      <!-- Canvas -->
      <canvas v-else ref="chartRef"></canvas>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue'
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  CategoryScale,
  Tooltip,
  Legend,
  Filler
} from 'chart.js'

Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  CategoryScale,
  Tooltip,
  Legend,
  Filler
)

export default {
  name: 'LineChart',

  props: {
    title: { type: String, default: 'Título del gráfico' },
    subtitle: { type: String, default: 'Subtítulo del gráfico' },
    total: { type: Number, default: 0 }, 
    labels: { type: Array, required: true },
    tooltipLabels: { type: Array, required: true },
    datasets: { type: Array, required: true }
  },

  setup(props) {
    const chartRef = ref(null)
    let chartInstance = null

    const renderChart = () => {
      if (!chartRef.value) return

      chartInstance = new Chart(chartRef.value, {
        type: 'line',
        data: {
          labels: props.labels,
          datasets: props.datasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              ticks: {
                color: '#64748b',
                font: { family: 'Manrope', size: 12 }
              },
              grid: { display: false }
            },
            y: {
              beginAtZero: true,
              ticks: {
                color: '#64748b',
                font: { family: 'Manrope', size: 12 }
              },
              grid: {
                color: 'rgba(100,116,139,0.1)',
                borderDash: [4, 4]
              }
            }
          },
          plugins: {
            legend: {
              position: 'top',
              labels: {
                color: '#1e293b',
                font: { family: 'Manrope', size: 13 }
              }
            },
            tooltip: {
              backgroundColor: '#1e293b',
              titleFont: { family: 'Manrope', size: 13 },
              bodyFont: { family: 'Manrope', size: 12 },
              padding: 10,
              callbacks: {
                title: (tooltipItems) => {
                  const index = tooltipItems[0]?.dataIndex
                  return props.tooltipLabels[index] || `Día ${index + 1}`
                }
              }
            }
          }
        }
      })
    }

    onMounted(() => renderChart())

    watch(
      () => [ props.labels, props.datasets ],
      ([newLabels, newDatasets]) => {
        if (!chartInstance) return
        chartInstance.data.labels   = newLabels
        chartInstance.data.datasets = newDatasets
        chartInstance.update()
      },
      { deep: true }
    );

    onBeforeUnmount(() => {
      if (chartInstance) {
        chartInstance.destroy()
      }
    })

    return {
      chartRef
    }
  }
}
</script>

<style scoped>
.line-chart-card {
  background-color: #ffffff;
  border-radius: 1.25rem;
  padding: 1.5rem;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.06);
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  max-height: 360px;
  margin-top: 2rem;
  font-family: 'Manrope', sans-serif;
}

.line-chart-card__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}

.header-left {
  display: flex;
  flex-direction: column;
  text-align: left;
}

.header-right {
  font-size: 0.95rem;
  font-weight: 600;
  color: #0f172a;
  background: #f1f5f9;
  padding: 0.4rem 0.8rem;
  border-radius: 0.5rem;
  border: 1px solid #e2e8f0;
}

.line-chart-card__title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.line-chart-card__subtitle {
  font-size: 0.95rem;
  font-weight: 500;
  color: #64748b;
  margin: 0;
}

/* Altura por estado */
.line-chart-card__canvas-wrapper {
  position: relative;
  width: 100%;
  height: 300px;      /* con datos */
}

.line-chart-card__canvas-wrapper.is-empty {
  height: 150px;      /* sin datos */
}

canvas {
  height: 100% !important;
  width: 100% !important;
}

/* ===== No Data ====== */
.line-chart-no-data {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: .5rem;
  text-align: center;
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
  border: 2px dashed #e2e8f0;
  border-radius: .75rem;
  padding: 1rem;
}

.line-no-data-illustration {
  width: 56px;
  height: 56px;
  border-radius: 9999px;
  background: #eef2ff;
  display: grid;
  place-items: center;
  box-shadow: inset 0 0 0 1px #e2e8f0;
}

.line-no-data-illustration ion-icon {
  font-size: 1.4rem;
  color: #64748b;
}

.line-no-data-title {
  margin: 0;
  font-weight: 700;
  font-size: .95rem;
  color: #0f172a;
}

.line-no-data-text {
  margin: 0;
  font-weight: 500;
  font-size: .85rem;
  color: #64748b;
}

</style>